import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SETTINGS } from "@/constants/appIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Store, Package, Users, FileSpreadsheet, Plus, Trash2, Upload as UploadIcon, CheckCircle2, XCircle, Loader2,
} from "lucide-react";

const ROLE_LABEL = { owner: "Pemilik", admin: "Admin Pemeriksa", uploader: "Pengunggah" };

export default function Settings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const tplRef = useRef();

  const { data: outlets = [] } = useQuery({ queryKey: ["outlets"], queryFn: async () => (await api.get("/outlets")).data });
  const { data: items = [] } = useQuery({ queryKey: ["master-items"], queryFn: async () => (await api.get("/master-items")).data });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data, enabled: user?.role === "owner" });
  const { data: drStatus } = useQuery({ queryKey: ["dr-status"], queryFn: async () => (await api.get("/daily-report/status")).data });
  const { data: preview } = useQuery({ queryKey: ["dr-preview"], queryFn: async () => (await api.get("/daily-report/preview")).data });

  const [newOutlet, setNewOutlet] = useState({ name: "", address: "" });
  const [newItem, setNewItem] = useState({ name: "", unit: "", category: "" });
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "uploader", outlet_ids: [] });
  const [uploadingTpl, setUploadingTpl] = useState(false);
  const saRef = useRef();
  const { data: gs } = useQuery({ queryKey: ["gsheets"], queryFn: async () => (await api.get("/gsheets/status")).data, refetchInterval: 12000, enabled: user?.role === "owner" });
  const [gsUrl, setGsUrl] = useState("");
  const [gsWs, setGsWs] = useState("RINCIAN PENGELUARAN");
  const [gsBusy, setGsBusy] = useState(false);
  const saveGsConfig = async () => {
    try { await api.post("/gsheets/config", { spreadsheet_url: gsUrl, worksheet: gsWs }); toast.success("Konfigurasi sheet disimpan"); qc.invalidateQueries({ queryKey: ["gsheets"] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const uploadSA = async (file) => {
    if (!file) return; setGsBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/gsheets/credentials", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(data.worksheet_found ? "Terhubung ke Google Sheets" : "Kredensial tersimpan"); qc.invalidateQueries({ queryKey: ["gsheets"] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGsBusy(false); }
  };
  const syncNow = async () => {
    setGsBusy(true);
    try { const { data } = await api.post("/gsheets/sync"); toast.success(`Tersinkron ${data.synced} baris ke Google Sheets`); qc.invalidateQueries({ queryKey: ["gsheets"] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGsBusy(false); }
  };
  const [colMap, setColMap] = useState(null);
  useEffect(() => { if (gs?.column_map && colMap === null) setColMap(gs.column_map); }, [gs, colMap]);
  const saveMapping = async () => {
    try { await api.post("/gsheets/mapping", { column_map: colMap }); toast.success("Pemetaan kolom disimpan"); qc.invalidateQueries({ queryKey: ["gsheets"] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addOutlet = async () => {
    if (!newOutlet.name) return;
    try { await api.post("/outlets", newOutlet); toast.success("Outlet ditambah"); setNewOutlet({ name: "", address: "" }); qc.invalidateQueries({ queryKey: ["outlets"] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addItem = async () => {
    if (!newItem.name) return;
    try { await api.post("/master-items", newItem); toast.success("Barang ditambah"); setNewItem({ name: "", unit: "", category: "" }); qc.invalidateQueries({ queryKey: ["master-items"] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delItem = async (id) => { await api.delete(`/master-items/${id}`); qc.invalidateQueries({ queryKey: ["master-items"] }); };
  const addUser = async () => {
    try { await api.post("/auth/register", newUser); toast.success("Pengguna ditambah"); setNewUser({ name: "", email: "", password: "", role: "uploader", outlet_ids: [] }); qc.invalidateQueries({ queryKey: ["users"] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadTemplate = async (file) => {
    if (!file) return;
    setUploadingTpl(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/daily-report/template", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template Daily Report diperbarui");
      qc.invalidateQueries({ queryKey: ["dr-status"] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploadingTpl(false); }
  };

  const toggleOutletForUser = (oid) => {
    setNewUser((u) => ({ ...u, outlet_ids: u.outlet_ids.includes(oid) ? u.outlet_ids.filter((x) => x !== oid) : [...u.outlet_ids, oid] }));
  };

  return (
    <div className="space-y-6 nh-fade">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Pengaturan</h1>
        <p className="text-sm text-muted-foreground">Kelola outlet, master barang, pengguna, dan koneksi Daily Report.</p>
      </div>

      <Tabs defaultValue="outlets">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="outlets" data-testid={SETTINGS.tabOutlets}><Store className="w-4 h-4 mr-1.5" /> Outlet</TabsTrigger>
          <TabsTrigger value="items" data-testid={SETTINGS.tabItems}><Package className="w-4 h-4 mr-1.5" /> Master Barang</TabsTrigger>
          {user?.role === "owner" && <TabsTrigger value="users" data-testid={SETTINGS.tabUsers}><Users className="w-4 h-4 mr-1.5" /> Pengguna</TabsTrigger>}
          <TabsTrigger value="daily" data-testid={SETTINGS.tabDaily}><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Daily Report</TabsTrigger>
        </TabsList>

        {/* Outlets */}
        <TabsContent value="outlets" className="space-y-4">
          {user?.role === "owner" && (
            <Card className="p-4 flex gap-2 flex-wrap items-end">
              <div className="flex-1 min-w-[160px]"><Label className="text-xs">Nama Outlet</Label><Input value={newOutlet.name} onChange={(e) => setNewOutlet({ ...newOutlet, name: e.target.value })} className="mt-1 h-9" /></div>
              <div className="flex-1 min-w-[160px]"><Label className="text-xs">Alamat</Label><Input value={newOutlet.address} onChange={(e) => setNewOutlet({ ...newOutlet, address: e.target.value })} className="mt-1 h-9" /></div>
              <Button onClick={addOutlet} data-testid={SETTINGS.addOutlet} className="h-9"><Plus className="w-4 h-4 mr-1" /> Tambah</Button>
            </Card>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            {outlets.map((o) => (
              <Card key={o.id} className="p-4">
                <div className="flex items-center justify-between">
                  <p className="font-medium">{o.name}</p>
                  {o.is_sample && <Badge variant="outline" className="text-[10px]">contoh</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{o.address || "—"}</p>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Items */}
        <TabsContent value="items" className="space-y-4">
          <Card className="p-4 grid gap-2 sm:grid-cols-4 items-end">
            <div><Label className="text-xs">Nama Barang</Label><Input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="mt-1 h-9" /></div>
            <div><Label className="text-xs">Satuan</Label><Input value={newItem.unit} onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })} className="mt-1 h-9" placeholder="kg, pcs…" /></div>
            <div>
              <Label className="text-xs">Kategori</Label>
              <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={addItem} data-testid={SETTINGS.addItem} className="h-9"><Plus className="w-4 h-4 mr-1" /> Tambah</Button>
          </Card>
          <Card className="divide-y divide-border">
            {items.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3">
                <div className="flex-1"><p className="text-sm font-medium">{m.name}</p><p className="text-xs text-muted-foreground">{m.unit || "—"} · {m.category || "—"}</p></div>
                {m.is_sample && <Badge variant="outline" className="text-[10px]">contoh</Badge>}
                <Button size="icon" variant="ghost" onClick={() => delItem(m.id)}><Trash2 className="w-4 h-4 text-muted-foreground" /></Button>
              </div>
            ))}
          </Card>
        </TabsContent>

        {/* Users */}
        {user?.role === "owner" && (
          <TabsContent value="users" className="space-y-4">
            <Card className="p-4 space-y-3">
              <div className="grid gap-2 sm:grid-cols-2">
                <div><Label className="text-xs">Nama</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="mt-1 h-9" /></div>
                <div><Label className="text-xs">Email</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="mt-1 h-9" /></div>
                <div><Label className="text-xs">Kata Sandi</Label><Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="mt-1 h-9" /></div>
                <div>
                  <Label className="text-xs">Peran</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                    <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="uploader">Pengunggah</SelectItem>
                      <SelectItem value="admin">Admin Pemeriksa</SelectItem>
                      <SelectItem value="owner">Pemilik</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Akses Outlet</Label>
                <div className="flex gap-2 flex-wrap mt-1">
                  {outlets.map((o) => (
                    <button key={o.id} onClick={() => toggleOutletForUser(o.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border ${newUser.outlet_ids.includes(o.id) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
                      {o.name}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={addUser} data-testid={SETTINGS.addUser}><Plus className="w-4 h-4 mr-1" /> Tambah Pengguna</Button>
            </Card>
            <Card className="divide-y divide-border">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 p-3">
                  <div className="flex-1"><p className="text-sm font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
                  <Badge variant="outline" className="text-[10px]">{ROLE_LABEL[u.role]}</Badge>
                  <span className="text-xs text-muted-foreground">{u.outlet_ids?.length || 0} outlet</span>
                </div>
              ))}
            </Card>
          </TabsContent>
        )}

        {/* Daily Report */}
        <TabsContent value="daily" className="space-y-4">
          <Card className="p-5">
            <div className="flex items-start gap-3">
              {drStatus?.external_connected ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" /> : <XCircle className="w-5 h-5 text-muted-foreground mt-0.5" />}
              <div className="flex-1">
                <p className="font-semibold">{drStatus?.external_connected ? "Daily Report terhubung (mode Excel)" : "Daily Report belum terhubung"}</p>
                <p className="text-sm text-muted-foreground mt-1">{drStatus?.external_message}</p>
                {drStatus?.template_name && <p className="text-xs font-mono mt-2 text-muted-foreground">Template: {drStatus.template_name}</p>}
              </div>
            </div>
            {user?.role === "owner" && (
              <div className="mt-4">
                <input ref={tplRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => uploadTemplate(e.target.files?.[0])} />
                <Button variant="outline" data-testid={SETTINGS.uploadTemplate} onClick={() => tplRef.current?.click()} disabled={uploadingTpl}>
                  {uploadingTpl ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadIcon className="w-4 h-4 mr-2" />} Unggah / Ganti Template (.xlsx)
                </Button>
              </div>
            )}
          </Card>

          {user?.role === "owner" && (
            <Card className="p-5">
              <div className="flex items-start gap-3">
                {gs?.connected ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" /> : <XCircle className="w-5 h-5 text-muted-foreground mt-0.5" />}
                <div className="flex-1">
                  <p className="font-semibold">Sinkron Google Sheets (otomatis saat disetujui)</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {gs?.connected
                      ? `Terhubung. Setiap nota yang disetujui langsung ditambahkan ke tab "${gs.worksheet}".`
                      : "Belum terhubung. Ikuti langkah di bawah untuk menghubungkan sheet Anda."}
                  </p>
                  {gs?.connected && (
                    <p className="text-xs text-muted-foreground mt-1 font-mono">
                      {gs.synced_count} baris tersinkron · {gs.pending_count} menunggu
                    </p>
                  )}
                </div>
                {gs?.connected && (
                  <Button size="sm" variant="outline" onClick={syncNow} disabled={gsBusy} data-testid="gsheets-sync">
                    {gsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sinkron sekarang"}
                  </Button>
                )}
              </div>

              <div className="mt-4 space-y-4 text-sm">
                <div className="rounded-lg bg-secondary/50 p-3 space-y-1.5 text-xs leading-relaxed">
                  <p className="font-semibold">Langkah setup (sekali saja):</p>
                  <p>1. Buka <a className="underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">Google Cloud Console</a> → buat Project.</p>
                  <p>2. APIs & Services → Library → aktifkan <b>Google Sheets API</b>.</p>
                  <p>3. APIs & Services → Credentials → <b>Create Credentials → Service Account</b>. Buat, lalu buka tab <b>Keys → Add Key → JSON</b> dan unduh file JSON.</p>
                  <p>4. Simpan URL sheet & nama tab di bawah, lalu unggah file JSON.</p>
                  <p>5. Setelah JSON diunggah, muncul email service account — <b>bagikan (Share) Google Sheet Anda ke email tersebut sebagai Editor</b>.</p>
                </div>

                <div className="grid gap-2 sm:grid-cols-3 items-end">
                  <div className="sm:col-span-2"><Label className="text-xs">URL Google Sheet</Label>
                    <Input value={gsUrl} onChange={(e) => setGsUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/..." className="mt-1 h-9" data-testid="gsheets-url" /></div>
                  <div><Label className="text-xs">Nama Tab</Label>
                    <Input value={gsWs} onChange={(e) => setGsWs(e.target.value)} className="mt-1 h-9" data-testid="gsheets-worksheet" /></div>
                </div>
                <Button size="sm" variant="outline" onClick={saveGsConfig} data-testid="gsheets-save-config">Simpan Konfigurasi Sheet</Button>

                <div>
                  <input ref={saRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => uploadSA(e.target.files?.[0])} />
                  <Button size="sm" onClick={() => saRef.current?.click()} disabled={gsBusy} data-testid="gsheets-upload-sa">
                    {gsBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadIcon className="w-4 h-4 mr-2" />} Unggah Kredensial Service Account (.json)
                  </Button>
                  {gs?.service_account_email && (
                    <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                      Bagikan sheet ke email ini (sebagai Editor): <span className="font-mono select-all">{gs.service_account_email}</span>
                      {gs.worksheets && !gs.connected && <p className="mt-1">Tab ditemukan: {gs.worksheets.join(", ") || "—"}. Pastikan sheet sudah dibagikan & nama tab benar.</p>}
                    </div>
                  )}
                </div>

                {gs?.has_credentials && colMap && (
                  <div className="pt-3 border-t border-border">
                    <p className="text-xs font-semibold mb-1">Pemetaan Kolom ke Tab Sheet Anda</p>
                    <p className="text-[11px] text-muted-foreground mb-2">Sesuaikan huruf kolom (A, B, C, …) agar cocok persis dengan tata letak tab. Kosongkan untuk melewati kolom.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(gs.field_defs || []).map((f) => (
                        <div key={f.key} className="flex items-center gap-2">
                          <span className="text-[11px] text-muted-foreground flex-1 truncate">{f.label}</span>
                          <Input value={colMap[f.key] ?? ""} onChange={(e) => setColMap({ ...colMap, [f.key]: e.target.value.toUpperCase() })} className="h-8 w-14 text-center font-mono" data-testid={`gsheets-col-${f.key}`} />
                        </div>
                      ))}
                    </div>
                    <Button size="sm" variant="outline" className="mt-3" onClick={saveMapping} data-testid="gsheets-save-mapping">Simpan Pemetaan Kolom</Button>
                  </div>
                )}
              </div>
            </Card>
          )}

          {preview && (
            <Card className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold">Pemetaan Kolom → Sheet "{preview.sheet}"</p>
                  <p className="text-xs text-muted-foreground">Struktur: per barang · {preview.total_entries} baris siap dikirim</p>
                </div>
              </div>
              <div className="mt-3 overflow-x-auto nh-scroll">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-3">Kolom Excel</th><th className="py-1.5 pr-3">Sumber Data</th>
                  </tr></thead>
                  <tbody>
                    {preview.column_map.map((c) => (
                      <tr key={c.letter} className="border-b border-border/60">
                        <td className="py-1.5 pr-3 font-mono">{c.letter} · {c.header}</td>
                        <td className="py-1.5 pr-3 text-muted-foreground">{c.source}</td>
                      </tr>
                    ))}
                    {preview.preserved_formula_columns.map((c) => (
                      <tr key={c.letter} className="border-b border-border/60 bg-secondary/40">
                        <td className="py-1.5 pr-3 font-mono">{c.letter} · {c.header}</td>
                        <td className="py-1.5 pr-3 text-muted-foreground italic">{c.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Rumus (Tanggal Acuan, Unit, Total Pengeluaran) dan format asli file Anda dipertahankan. Hasil berupa file .xlsx
                terisi yang bisa diunduh dari halaman Riwayat.
              </p>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
